import { nanoid } from 'nanoid';

import { type SelectColumnsEvent } from '../events/types';
import { type SelectionRange, type SelectionState } from '../state/types';

import { type ReducerResponse } from './types';

/**
 * Replaces selection state with finite column ranges and positions focus on the
 * first range. Empty input is a noop; every stored range receives a stable ID.
 */
export function selectColumnsReducer(
  state: SelectionState,
  event: SelectColumnsEvent
): ReducerResponse {
  // No-op if no ranges provided
  if (event.ranges.length === 0) {
    return {
      delta: { kind: 'noop' },
      next: state
    };
  }

  // Map event ranges to internal SelectionRange objects
  // In Column Mode, the 'row' coordinate for selection ranges is symbolic
  // (locked to 0) because the renderer draws them full-height.
  const newSelections: SelectionRange[] = event.ranges.map((range) => ({
    id: nanoid(),
    anchor: {
      row: { index: 0 },
      col: range.anchor.col
    },
    lead: {
      row: { index: 0 },
      col: range.lead.col
    }
  }));

  // Determine Focus
  // We grab the column from the first range's lead (active end).
  const primaryRange = event.ranges[0];

  const focusedCell = {
    row: event.focusRow ?? { index: 0 }, // Use provided row or default to 0
    col: primaryRange.lead.col
  };

  return {
    delta: {
      kind: 'selectionReplaced',
      source: event.source,
      selections: newSelections,
      focusChange: {
        prev: state.focusedCell,
        next: focusedCell
      }
    },

    next: {
      ...state,
      mode: 'column',
      focusedCell,
      selections: newSelections,
      lastInteractionSource: event.source
    }
  };
}
